import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { icon } from "../../../config";

/**
 * DropdownColumnSubMenu
 * Sub-menu component for dropdown column type configuration
 * @param {Object} column - Column configuration object
 * @param {number} columnIndex - Index of the column
 * @param {Function} onColumnChange - Callback for column changes
 */
const DropdownColumnSubMenu = ({ column, columnIndex, onColumnChange }) => {
  const [optionInputValues, setOptionInputValues] = useState(() => {
    const initialValues = {};
    (column.options || []).forEach((option) => {
      initialValues[option.id] = option.value || "";
    });
    return initialValues;
  });

  // Sync local state when new options are added/removed from parent
  useEffect(() => {
    const optionValuesFromProps = {};
    (column.options || []).forEach((option) => {
      optionValuesFromProps[option.id] = option.value || "";
    });

    setOptionInputValues((prevValues) => {
      const mergedValues = { ...prevValues };
      Object.keys(optionValuesFromProps).forEach((optionId) => {
        if (!prevValues.hasOwnProperty(optionId)) {
          mergedValues[optionId] = optionValuesFromProps[optionId];
        }
      });
      return mergedValues;
    });
  }, [column.options?.length]);

  const handleAddOption = () => {
    const currentOptions = column.options || [];
    if (currentOptions.length >= 10) return;

    const newOption = {
      id: Date.now(),
      value: "",
    };

    const updatedColumn = {
      ...column,
      options: [...currentOptions, newOption],
    };

    onColumnChange(columnIndex, updatedColumn);
  };

  const handleOptionValueChange = (optionId, newValue) => {
    if (newValue.length > 40) return; // Max 40 characters

    setOptionInputValues((prevValues) => ({
      ...prevValues,
      [optionId]: newValue,
    }));

    const updatedOptions = column.options.map((option) => (option.id === optionId ? { ...option, value: newValue } : option));

    const updatedColumn = {
      ...column,
      options: updatedOptions,
    };

    onColumnChange(columnIndex, updatedColumn);
  };

  const handleRemoveOption = (optionId) => {
    const filteredOptions = column.options.filter((option) => option.id !== optionId);

    setOptionInputValues((prevValues) => {
      const updatedValues = { ...prevValues };
      delete updatedValues[optionId];
      return updatedValues;
    });

    const updatedColumn = {
      ...column,
      options: filteredOptions,
    };

    onColumnChange(columnIndex, updatedColumn);
  };

  return (
    <div className='mt-2 bg-light'>
      <div className='mb-2'>
        <span className='form-label small'>Add Options</span>
      </div>

      {/* Display existing options */}
      {(column.options || []).map((option, optionIndex) => (
        <div key={option.id} className='d-flex align-items-center mb-2'>
          <input
            type='text'
            value={optionInputValues[option.id] || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              handleOptionValueChange(option.id, newValue);
            }}
            placeholder={`Option ${optionIndex + 1}`}
            maxLength={40}
            className='form-control form-control-sm flex-grow-1 me-2'
          />
          <button type='button' onClick={() => handleRemoveOption(option.id)} className='btn btn-sm red' title='Remove option'>
            {icon.DELETE_ICON}
          </button>
        </div>
      ))}

      {/* Add option button */}
      {(!column.options || column.options.length < 10) && (
        <button type='button' onClick={handleAddOption} className='btn btn-sm btn-outline-primary'>
          <Plus className='lucid-icon-common me-1 font-size-16 ' />
          Add Option
        </button>
      )}

      {/* Display limits */}
      <div className='mt-2'>
        <small className='text-muted'>{(column.options || []).length}/10 options • Max 40 characters per option</small>
      </div>
    </div>
  );
};

export default DropdownColumnSubMenu;
